import type { LoadingStore } from '../loading'

export interface ApiErrorOptions {
  status?: number
  payload?: unknown
  url?: string
}

export class ApiError extends Error {
  status: number | undefined
  payload: unknown
  url: string | undefined

  constructor(message: string, { status, payload, url }: ApiErrorOptions = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
    this.url = url
  }
}

export interface ApiRequestOptions {
  method?: string
  token?: string | null
  body?: unknown
  signal?: AbortSignal
  timeoutMs?: number
  headers?: Record<string, string>
  correlationId?: string
  cacheKey?: string
  cacheTtlMs?: number
  skipGlobalLoading?: boolean
  blockingLoading?: boolean
  loadingLabel?: string
  loadingSource?: string
  loadingType?: string
}

export interface ApiClientOptions {
  apiBase: string
  defaultTimeoutMs?: number
  correlationIdKey?: string
  loadingStore: LoadingStore
  onNetworkOnline?: () => void
  onNetworkOffline?: () => void
  onAuthRejected?: (context: { path: string; method: string; status: number; url: string }) => void
  inferBlockingLoading?: (path: string, method: string, options: ApiRequestOptions) => boolean
}

const createAbortSignal = (externalSignal?: AbortSignal, timeoutMs?: number) => {
  const hasTimeout = Number.isFinite(timeoutMs) && Number(timeoutMs) > 0
  if (!externalSignal && !hasTimeout) {
    return { signal: undefined, cleanup: () => {} }
  }

  const controller = new AbortController()
  let timeoutId: number | null = null

  const onAbort = () => controller.abort()
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort()
    } else {
      externalSignal.addEventListener('abort', onAbort, { once: true })
    }
  }

  if (hasTimeout) {
    // Abort with a distinct TimeoutError reason so a genuine timeout stays
    // distinguishable from a caller/navigation cancellation (a bare abort).
    // Real timeouts remain visible in diagnostics; cancellations are dropped.
    timeoutId = window.setTimeout(
      () => controller.abort(new DOMException(`Request timed out after ${Number(timeoutMs)}ms`, 'TimeoutError')),
      Number(timeoutMs)
    )
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      if (timeoutId != null) {
        window.clearTimeout(timeoutId)
      }
      if (externalSignal) {
        externalSignal.removeEventListener('abort', onAbort)
      }
    },
  }
}

const parseResponse = async (response: Response) => {
  if (response.status === 204) {
    return null
  }
  const contentType = response.headers.get('content-type') || ''
  const text = await response.text()
  if (!text) {
    return null
  }
  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text)
    } catch {
      return null
    }
  }
  return text
}

const getCorrelationId = (storageKey: string) => {
  if (typeof window === 'undefined') {
    return null
  }
  const stored = window.sessionStorage.getItem(storageKey)
  if (stored) {
    return stored
  }
  const generated = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `cid-${Math.random().toString(16).slice(2)}-${Date.now()}`
  window.sessionStorage.setItem(storageKey, generated)
  return generated
}

const getCacheNamespace = (token?: string | null) => {
  if (!token) {
    return 'anon'
  }
  let hash = 0
  for (let index = 0; index < token.length; index += 1) {
    hash = (hash << 5) - hash + token.charCodeAt(index)
    hash |= 0
  }
  return `t${Math.abs(hash)}`
}

const isNetworkError = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return false
  }
  const candidate = error as Error
  if (candidate.name === 'AbortError' || candidate.name === 'TimeoutError') {
    return false
  }
  if (candidate.name === 'TypeError') {
    return true
  }
  return /network|failed to fetch|connection refused|err_connection_refused/i.test(String(candidate.message || ''))
}

export function createApiClient({
  apiBase,
  defaultTimeoutMs = 30000,
  correlationIdKey = 'cargonice-correlation-id',
  loadingStore,
  onNetworkOffline,
  onNetworkOnline,
  onAuthRejected,
  inferBlockingLoading,
}: ApiClientOptions) {
  const cacheStore = new Map<string, { value: unknown; expiresAt: number }>()
  const inFlight = new Map<string, Promise<unknown>>()

  const toApiRequestOptions = (options: ApiRequestOptions): ApiRequestOptions => ({
    ...(options.method !== undefined ? { method: options.method } : {}),
    ...(options.token !== undefined ? { token: options.token } : {}),
    ...(options.body !== undefined ? { body: options.body } : {}),
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
    ...(options.timeoutMs !== undefined ? { timeoutMs: options.timeoutMs } : {}),
    ...(options.headers !== undefined ? { headers: options.headers } : {}),
    ...(options.correlationId !== undefined ? { correlationId: options.correlationId } : {}),
    ...(options.cacheKey !== undefined ? { cacheKey: options.cacheKey } : {}),
    ...(options.cacheTtlMs !== undefined ? { cacheTtlMs: options.cacheTtlMs } : {}),
    ...(options.skipGlobalLoading !== undefined ? { skipGlobalLoading: options.skipGlobalLoading } : {}),
    ...(options.blockingLoading !== undefined ? { blockingLoading: options.blockingLoading } : {}),
    ...(options.loadingLabel !== undefined ? { loadingLabel: options.loadingLabel } : {}),
    ...(options.loadingSource !== undefined ? { loadingSource: options.loadingSource } : {}),
    ...(options.loadingType !== undefined ? { loadingType: options.loadingType } : {}),
  })

  const clearApiCache = () => {
    cacheStore.clear()
    inFlight.clear()
  }

  const invalidateApiCache = (key?: string, token?: string | null) => {
    if (!key) {
      return
    }
    if (token) {
      const effectiveKey = `${key}:${getCacheNamespace(token)}`
      cacheStore.delete(effectiveKey)
      inFlight.delete(effectiveKey)
      return
    }
    for (const storedKey of cacheStore.keys()) {
      if (storedKey.startsWith(`${key}:`)) {
        cacheStore.delete(storedKey)
      }
    }
    for (const storedKey of inFlight.keys()) {
      if (storedKey.startsWith(`${key}:`)) {
        inFlight.delete(storedKey)
      }
    }
  }

  const apiRequest = async <T = unknown>(
    path: string,
    {
      method = 'GET',
      token,
      body,
      signal,
      timeoutMs = defaultTimeoutMs,
      headers: extraHeaders,
      correlationId: explicitCorrelationId,
      cacheKey,
      cacheTtlMs = 0,
      skipGlobalLoading = false,
      blockingLoading,
      loadingLabel,
      loadingSource = 'api',
      loadingType,
    }: ApiRequestOptions = {}
  ): Promise<T> => {
    const upperMethod = method.toUpperCase()
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(extraHeaders || {}),
    }

    let payload: BodyInit | undefined
    if (body !== undefined) {
      if (body instanceof FormData) {
        payload = body
      } else if (typeof body === 'string') {
        payload = body
        if (!headers['Content-Type']) {
          headers['Content-Type'] = 'application/json'
        }
      } else {
        headers['Content-Type'] = 'application/json'
        payload = JSON.stringify(body)
      }
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    const correlationId =
      typeof explicitCorrelationId === 'string' && explicitCorrelationId.trim()
        ? explicitCorrelationId.trim()
        : getCorrelationId(correlationIdKey)
    if (correlationId) {
      headers['X-Correlation-Id'] = correlationId
    }

    const isGet = upperMethod === 'GET'
    const shouldCache = isGet && Boolean(cacheKey)
    const effectiveCacheKey = shouldCache ? `${cacheKey}:${getCacheNamespace(token)}` : null
    const allowDedupe = Boolean(shouldCache && effectiveCacheKey && !signal)

    if (allowDedupe && effectiveCacheKey) {
      const cached = cacheStore.get(effectiveCacheKey)
      if (cached) {
        if (cached.expiresAt > Date.now()) {
          return cached.value as T
        }
        cacheStore.delete(effectiveCacheKey)
      }
      if (inFlight.has(effectiveCacheKey)) {
        return inFlight.get(effectiveCacheKey) as Promise<T>
      }
    }

    const executeRequest = async (): Promise<T> => {
      const inferredRequestOptions = toApiRequestOptions({
        ...(upperMethod !== undefined ? { method: upperMethod } : {}),
        ...(token !== undefined ? { token } : {}),
        ...(body !== undefined ? { body } : {}),
        ...(signal !== undefined ? { signal } : {}),
        ...(timeoutMs !== undefined ? { timeoutMs } : {}),
        ...(extraHeaders !== undefined ? { headers: extraHeaders } : {}),
        ...(explicitCorrelationId !== undefined ? { correlationId: explicitCorrelationId } : {}),
        ...(cacheKey !== undefined ? { cacheKey } : {}),
        ...(cacheTtlMs !== undefined ? { cacheTtlMs } : {}),
        ...(skipGlobalLoading !== undefined ? { skipGlobalLoading } : {}),
        ...(blockingLoading !== undefined ? { blockingLoading } : {}),
        ...(loadingLabel !== undefined ? { loadingLabel } : {}),
        ...(loadingSource !== undefined ? { loadingSource } : {}),
        ...(loadingType !== undefined ? { loadingType } : {}),
      })

      const loadingToken = skipGlobalLoading
        ? null
        : loadingStore.startLoading({
            blocking:
              blockingLoading ??
              inferBlockingLoading?.(
                path,
                upperMethod,
                inferredRequestOptions
              ) ??
              false,
            ...(loadingLabel !== undefined ? { label: loadingLabel } : {}),
            source: loadingSource,
            type: loadingType ?? upperMethod.toLowerCase(),
          })

      const { signal: mergedSignal, cleanup } = createAbortSignal(signal, timeoutMs)
      try {
        const requestInit: RequestInit = {
          method: upperMethod,
          headers,
          ...(payload !== undefined ? { body: payload } : {}),
          ...(mergedSignal !== undefined ? { signal: mergedSignal } : {}),
        }
        const response = await fetch(`${apiBase}${path}`, {
          ...requestInit,
        })

        onNetworkOnline?.()

        const data = await parseResponse(response)
        if (!response.ok) {
          if (token && (response.status === 401 || response.status === 403)) {
            try {
              onAuthRejected?.({
                path,
                method: upperMethod,
                status: response.status,
                url: `${apiBase}${path}`,
              })
            } catch {
              // Auth cleanup must not hide the original API error.
            }
          }
          const message =
            (data && typeof data === 'object' && 'error' in data && (data as { error?: string }).error) ||
            (data && typeof data === 'object' && 'message' in data && (data as { message?: string }).message) ||
            'Request failed'
          throw new ApiError(String(message), {
            status: response.status,
            payload: data,
            url: `${apiBase}${path}`,
          })
        }

        if (allowDedupe && effectiveCacheKey && cacheTtlMs > 0) {
          cacheStore.set(effectiveCacheKey, {
            value: data,
            expiresAt: Date.now() + cacheTtlMs,
          })
        }

        return data as T
      } catch (error) {
        if (isNetworkError(error)) {
          onNetworkOffline?.()
        }
        throw error
      } finally {
        cleanup()
        if (loadingToken) {
          loadingStore.stopLoading(loadingToken)
        }
      }
    }

    if (!allowDedupe || !effectiveCacheKey) {
      return executeRequest()
    }

    const requestPromise = executeRequest()
    inFlight.set(effectiveCacheKey, requestPromise)
    try {
      return await requestPromise
    } finally {
      inFlight.delete(effectiveCacheKey)
    }
  }

  return {
    getApiBase: () => apiBase,
    ApiError,
    apiRequest,
    clearApiCache,
    invalidateApiCache,
  }
}
